import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from '../../config/axiosConfig';
import {
  Bell,
  Plus,
  TrendingUp,
  Newspaper,
  Landmark,
  Building2,
  CalendarClock,
  ListChecks,
} from 'lucide-react';

const MiniAlertsSummary = ({ user }) => {
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Each alert type gets its own icon + color (color used for icon only)
  const alertTypeConfig = {
    priceAlert: {
      name: 'Price',
      icon: TrendingUp,
      color: '#0eb35b',
    },
    congressTrade: {
      name: 'Congress',
      icon: Landmark,
      color: '#8b5cf6',
    },
    newsAlert: {
      name: 'News',
      icon: Newspaper,
      color: '#3b82f6',
    },
    insiderTrade: {
      name: 'Insider',
      icon: Building2,
      color: '#f59e0b',
    },
    marketEvent: {
      name: 'Market',
      icon: CalendarClock,
      color: '#ec4899',
    },
  };

  useEffect(() => {
    if (!user?._id) return;

    const fetchMiniAlertsData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(`/api/alerts/mini/${user._id}`);
        setData(response.data);
      } catch (err) {
        console.error('Error fetching mini alerts data:', err);
        setError('Failed to load alerts summary');
      } finally {
        setLoading(false);
      }
    };

    fetchMiniAlertsData();
  }, [user?._id]);

  const handleCreateAlert = () => navigate('/alerts/new');
  const handleViewAlerts = () => navigate('/alerts');

  const getAlertLimitInfo = () => {
    const limit = user?.plan?.alertLimit || 5;
    const current = data?.activeAlerts?.total || 0;
    if (limit === -1) return { text: `${current}/∞`, limitReached: false };
    return { text: `${current}/${limit}`, limitReached: current >= limit };
  };

  // Loading
  if (loading) {
    return (
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Card>
    );
  }

  // Error
  if (error) {
    return (
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Alert severity="error">{error}</Alert>
        </CardContent>
      </Card>
    );
  }

  // Empty state
  if (!data || data.activeAlerts.total === 0) {
    return (
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          background: (theme) => theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.06) 0%, rgba(236, 72, 153, 0.04) 100%)'
            : 'linear-gradient(135deg, rgba(59, 130, 246, 0.04) 0%, rgba(236, 72, 153, 0.03) 100%)',
        }}
      >
        <CardContent
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            py: 2,
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(59, 130, 246, 0.12)',
              mb: 1.5,
            }}
          >
            <Bell size={22} color="#3b82f6" strokeWidth={1.75} />
          </Box>
          <Typography sx={{ fontSize: '1rem', fontWeight: 600, mb: 0.5 }}>
            Never Miss a Move
          </Typography>
          <Typography
            sx={{
              fontSize: '0.8rem',
              color: 'text.secondary',
              maxWidth: 260,
              mb: 2,
              lineHeight: 1.5,
            }}
          >
            Get notified on price moves, insider trades, and more.
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<Plus size={14} strokeWidth={2.5} />}
            onClick={handleCreateAlert}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' },
            }}
          >
            Create Alert
          </Button>
        </CardContent>
      </Card>
    );
  }

  const { activeAlerts, recentTriggers, notifications } = data;
  const limitInfo = getAlertLimitInfo();

  // Show all 5 alert types always — muted when count is 0
  // Keeps card visually balanced for free users and hints at other alert types
  const allAlertTypes = Object.keys(alertTypeConfig).map((type) => ({
    type,
    count: activeAlerts.byType?.[type] || 0,
  }));

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: { xs: 2, sm: 2.5 } }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography sx={{ fontSize: { xs: '1rem', sm: '1.15rem' }, fontWeight: 600 }}>
            Alert Activity
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
            <Button
              size="small"
              variant="text"
              onClick={handleViewAlerts}
              startIcon={<ListChecks size={14} strokeWidth={2.25} />}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                px: 1,
                py: 0.4,
                minHeight: 28,
              }}
            >
              View All
            </Button>
            <Tooltip title={limitInfo.limitReached ? 'Alert limit reached' : 'Create alert'}>
              <span>
                <IconButton
                  size="small"
                  onClick={handleCreateAlert}
                  disabled={limitInfo.limitReached}
                  sx={{
                    width: 28,
                    height: 28,
                    backgroundColor: 'primary.main',
                    color: 'white',
                    '&:hover': { backgroundColor: 'primary.dark' },
                    '&.Mui-disabled': {
                      backgroundColor: 'action.disabledBackground',
                      color: 'action.disabled',
                    },
                  }}
                >
                  <Plus size={16} strokeWidth={2.5} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>

        {/* Stats row — 3 compact metrics */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 1,
            mb: 2.5,
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              sx={{
                color: 'primary.main',
                fontWeight: 700,
                fontSize: { xs: '1.1rem', sm: '1.25rem' },
                lineHeight: 1.2,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {activeAlerts.total}
            </Typography>
            <Typography sx={{ fontSize: { xs: '0.6rem', sm: '0.68rem' }, color: 'text.secondary', mt: 0.25 }}>
              Active
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: '0.58rem', sm: '0.62rem' },
                color: limitInfo.limitReached ? '#e62333' : 'text.secondary',
                fontVariantNumeric: 'tabular-nums',
                fontWeight: limitInfo.limitReached ? 600 : 400,
              }}
            >
              {limitInfo.text}
            </Typography>
          </Box>

          <Box sx={{ textAlign: 'center' }}>
            <Typography
              sx={{
                color: recentTriggers > 0 ? '#0eb35b' : 'text.secondary',
                fontWeight: 700,
                fontSize: { xs: '1.1rem', sm: '1.25rem' },
                lineHeight: 1.2,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {recentTriggers}
            </Typography>
            <Typography sx={{ fontSize: { xs: '0.6rem', sm: '0.68rem' }, color: 'text.secondary', mt: 0.25 }}>
              Triggered
            </Typography>
          </Box>

          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 0.25 }}>
              <Typography
                sx={{
                  color: notifications.unread > 0 ? '#e62333' : 'text.secondary',
                  fontWeight: 700,
                  fontSize: { xs: '1.1rem', sm: '1.25rem' },
                  lineHeight: 1.2,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {notifications.unread}
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: '0.8rem', sm: '0.9rem' },
                  color: 'text.secondary',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                /{notifications.total}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: { xs: '0.6rem', sm: '0.68rem' }, color: 'text.secondary', mt: 0.25 }}>
              Unread / Total
            </Typography>
          </Box>
        </Box>

        {/* Active by type */}
        <Box
          sx={{
            mt: 'auto',
            pt: 1.5,
            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: '0.68rem', sm: '0.7rem' },
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              mb: 1,
              textAlign: 'center',
            }}
          >
            Active by Type
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, justifyContent: 'center' }}>
            {allAlertTypes.map(({ type, count }) => {
              const config = alertTypeConfig[type];
              if (!config) return null;
              const Icon = config.icon;
              const isActive = count > 0;
              return (
                <Chip
                  key={type}
                  size="small"
                  icon={
                    <Icon
                      size={12}
                      color={isActive ? config.color : undefined}
                      strokeWidth={2.25}
                    />
                  }
                  label={
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                      <span>{config.name}</span>
                      <Box
                        component="span"
                        sx={{
                          fontWeight: isActive ? 700 : 500,
                          color: isActive ? 'text.primary' : 'text.disabled',
                        }}
                      >
                        {count}
                      </Box>
                    </Box>
                  }
                  variant="outlined"
                  sx={{
                    fontSize: { xs: '0.68rem', sm: '0.72rem' },
                    height: { xs: 24, sm: 26 },
                    opacity: isActive ? 1 : 0.55,
                    color: isActive ? 'text.primary' : 'text.secondary',
                    '& .MuiChip-icon': {
                      marginLeft: '6px',
                      marginRight: '-2px',
                      color: isActive ? undefined : 'text.disabled',
                    },
                    '& .MuiChip-label': {
                      px: 0.75,
                      fontWeight: 500,
                    },
                  }}
                />
              );
            })}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default MiniAlertsSummary;